import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  DropDownInput,
  InputField,
  OptionGroup,
  RadioInput,
  DateInput,
  MiniTabs,
  DataTable,
} from "@/components";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import { BsBoxes } from "react-icons/bs";
import { RiFileExcel2Line } from "react-icons/ri";
import NDRForm from "./NDRForm";
import ColumnHelper from "@/utils/ColumnHelper";
import { useFilter } from "@/";
import { NDRHistory } from "..";
import { SpaceWrapper } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { fetchNDRData, selectAllNDRs } from "../../app/slices";
import { subDays } from "date-fns";
// Predefined options to avoid duplication

const paymentOptions = [
  { value: "Prepaid", label: "Prepaid" },
  { value: "COD", label: "COD" },
];

const bulkOptions = [
  { label: "Bulk NDR", value: "bulk_ndr", icon: <BsBoxes /> },
];

const Ndr = () => {
  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};

  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;

  // General state
  const [activeTab, setActiveTab] = useState("all");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  // Initial filters
  const [filters, setFilters] = useState({
    orderId: "",
    productName: "",
    channelName: "", // ✅ Keep the correct key
    courierName: "", // ✅ Match filterConfig key
    paymentMode: "",
    awbNumber: "",
    date: { from: startOfMonthDate, to: endOfMonthDate },
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);

  const courier = useSelector((state) => state.app?.appData?.courier) || [];
  const channel = useSelector((state) => state.app?.appData?.channels) || [];
  const dispatch = useDispatch();

  const fetchData = async () => {
    const response = await dispatch(
      fetchNDRData({
        startDate: filters.date.from,
        endDate: filters.date.to,
      })
    );
    console.log("response::::::", response);
  };

  useEffect(() => {
    fetchData();
  }, [appliedFilters.date, dispatch]);

  const NDRs = useSelector(selectAllNDRs) || [];

  // General filter configuration.
  const filterConfig = useMemo(
    () => ({
      orderId: {
        type: "comma-api",
        itemKey: "userOrderId",
        model: "Shipment",
        endpoint: "/common/search",
      },
      productName: { type: "partial", itemKey: "productName" },
      awbNumber: {
        type: "comma-api",
        itemKey: "awbNumber",
        model: "Shipment",
        endpoint: "/common/search",
      },
      channelName: { type: "exact", itemKey: "order.channel" },
      courierName: { type: "exact", itemKey: "courierId" },
      paymentMode: { type: "exact", itemKey: "paymentMode" },
      date: { type: "date", itemKey: "ndrRecords.ndrDate" },
    }),
    []
  );

  // Tab filter configuration as a serializable object.
  const tabConfig = useMemo(
    () => ({
      idKey: "ndrStatus",
      allTabId: "all",
      filters: {
        actionRequired: { type: "exact", value: "Exception" },
        actionRequested: { type: "exact", value: "Resolved" },
        delivered: { type: "exact", value: "Delivered" },
        rto: { type: "exact", value: "RTO" },
      },
    }),
    []
  );

  // Use the combined asynchronous filtering hook.
  const {
    data: finalFilteredData,
    counts,
    isLoading,
  } = useFilter(NDRs, appliedFilters, filterConfig, activeTab, tabConfig);

  const courierOptions = useMemo(
    () => courier.map((c) => ({ value: c._id, label: c.name })),
    [courier]
  );

  const channelOptions = useMemo(
    () => channel.map((c) => ({ value: c.name, label: c.name })),
    [channel]
  );

  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const statusStyle = {
    Pending: "bg-yellow-100/90 text-yellow-800",
    Pickup: "bg-yellow-100/90 text-yellow-800",
    "In Transit": "bg-orange-100 text-orange-800",
    Rescheduled: "bg-orange-100 text-orange-800",
    "Out for Delivery": "bg-purple-200/80 text-purple-800",
    Delivered: "bg-green-300/25 text-green-800",
    "Partially Delivered": "bg-green-300/25 text-green-800",
    Cancelled: "bg-cyan-800/20 text-cyan-800",
    Lost: "bg-slate-900/20 text-slate-900",
    Damaged: "bg-amber-900/20 text-amber-900",
    "RTO All": "bg-red-300/30 text-red-800",
    Exception: "bg-red-300/30 text-red-800",
    RTO: "bg-red-300/30 text-red-800",
    "RTO In-Transit": "bg-orange-100 text-orange-800",
    "RTO Delivered": "bg-green-100 text-green-800",
    "RTO Lost": "bg-slate-900/20 text-slate-900",
  };

  // Table columns.
  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Channel",
        accessorKey: "order.channel",
        meta: {
          tooltip: (row) => row.order?.channel || "N/A",
        },
      }),
      ColumnHelper({
        header: "Order ID",
        accessorKey: "userOrderId",
        meta: {
          tooltip: (row) => row.userOrderId,
        },
      }),
      ColumnHelper({
        header: "Booking Date",
        accessorKey: "shipmentDate",
        cell: (info) => formatDate(info.row.original.shipmentDate),
        meta: {
          tooltip: (row) => formatDate(row.shipmentDate),
        },
      }),
      ColumnHelper({
        header: "Payment",
        accessorKey: "totalCharge",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.totalCharge)}
          </span>
        ),
        meta: {
          tooltip: (row) => formatCurrency(row.totalCharge),
        },
      }),
      ColumnHelper({
        header: "Payment Mode",
        accessorKey: "paymentMode",
        meta: {
          tooltip: (row) => row.paymentMode,
        },
      }),
      ColumnHelper({
        header: "Customer",
        accessorKey: "deliveryAddress.customerName",
        meta: {
          tooltip: (row) => row.deliveryAddress?.customerName,
        },
      }),
      ColumnHelper({
        header: "Courier",
        accessorKey: "courierId",
        cell: (info) =>
          courier.find((c) => c._id === info.row.original.courierId)?.name,
        meta: {
          tooltip: (row) => courier.find((c) => c._id === row.courierId)?.name,
        },
      }),
      ColumnHelper({
        header: "AWB",
        accessorKey: "awbNumber",
        meta: {
          tooltip: (row) => row.awbNumber || "N/A",
        },
      }),
      ColumnHelper({
        header: "Status",
        accessorKey: "status",
        cell: (info) => {
          const status =
            info.row.original.ndrStatus === "Resolved"
              ? "Action Requested"
              : info.row.original.ndrStatus === "Exception"
              ? "Action Required"
              : info.row.original.ndrStatus;
          return (
            <span
              className={`px-2 py-1 rounded-md text-xs font-medium leading-3 ${
                statusStyle[info.row.original.ndrStatus]
              }`}
            >
              {status}
            </span>
          );
        },
        meta: {
          tooltip: (row) =>
            row.ndrStatus === "Resolved"
              ? "Action Requested"
              : row.ndrStatus === "Exception"
              ? "Action Required"
              : row.ndrStatus,
        },
      }),
      ColumnHelper({
        header: "Exception Info",
        accessorKey: "exceptionInfo",
        cell: (info) => {
          const records = info.row.original.ndrRecords || [];
          const allInteractions = records.flatMap((record) =>
            (record.interactions || []).map((interaction) => ({
              ...interaction,
              attempt: record.attempt,
            }))
          );
          return (
            <AlertDialog key={info.getValue()}>
              <AlertDialogTrigger>
                <p className="w-full text-ternary/80 line-clamp-1 underline cursor-pointer">
                  {info.row.original.ndrRecords?.[
                    info.row.original.ndrRecords.length - 1
                  ]?.lastCommunication?.message || "No Exception Info"}
                </p>
              </AlertDialogTrigger>
              <AlertDialogContent
                aria-describedby=""
                className="w-[60%] max-w-[60%] p-0 gap-1 bg-background max-h-[80vh] overflow-y-auto"
              >
                <AlertDialogHeader>
                  <AlertDialogTitle className="px-2 pt-2 text-headline4 font-nunito text-secondary/80 font-bold">
                    NDR History
                  </AlertDialogTitle>
                  <div className="!overflow-y-auto max-h-[60vh]">
                    <NDRHistory data={allInteractions || []} />
                  </div>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-secondary/10 hover:bg-secondary/10 rounded-xl mr-2 mb-2 text-secondary/90">
                    Cancel
                  </AlertDialogCancel>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          );
        },
        meta: {
          tooltip: (row) => {
            return (
              row.ndrRecords?.[row.ndrRecords.length - 1]?.lastCommunication
                ?.message || "No Exception Info"
            );
          },
        },
      }),
      ColumnHelper({
        header: "Action",
        accessorKey: "id",
        cell: (info) => {
          return (
            <div className="flex gap-1 justify-center items-center w-full min-w-28">
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    disabled={info.row.original.ndrStatus !== "Exception"}
                    size="xs"
                  >
                    Take Action
                  </Button>
                </SheetTrigger>
                <SheetContent aria-describedby="">
                  <SheetHeader>
                    <SheetTitle>NDR Submit Form</SheetTitle>
                    <NDRForm ids={[info.row.original._id]} />
                  </SheetHeader>
                </SheetContent>
              </Sheet>
            </div>
          );
        },
        meta: {
          tooltip: () => "Take action on this order",
        },
      }),
    ],
    [courier, formatCurrency, formatDate]
  );

  // Build tabs using counts returned by the worker.
  const tabs = useMemo(
    () => [
      { id: "all", label: "All", count: counts.all || 0 },
      {
        id: "actionRequired",
        label: "Action Required",
        count: counts.actionRequired || 0,
      },
      {
        id: "actionRequested",
        label: "Action Requested",
        count: counts.actionRequested || 0,
      },
      {
        id: "delivered",
        label: "Delivered",
        count: counts.delivered || 0,
      },
      {
        id: "rto",
        label: "RTO",
        count: counts.rto || 0,
      },
    ],
    [counts]
  );

  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDateChange = useCallback((dateRange) => {
    setFilters((prev) => ({
      ...prev,
      date: { from: dateRange?.from || "", to: dateRange?.to || "" },
    }));
  }, []);

  const handleApply = useCallback(() => {
    console.log("Filters applied:", filters);
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  const handleReset = useCallback(() => {
    const initialFilters = {
      orderId: "",
      productName: "",
      channelName: "",
      awbNumber: "",
      courierName: "",
      paymentMode: "",
      date: { from: startOfMonthDate, to: endOfMonthDate },
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, []);

  const handleTabChange = useCallback((tab) => {
    console.log("Tab Changed:", tab);
    setActiveTab(tab);
  }, []);

  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        {/* Header Section */}
        <div className="w-full flex flex-col gap-2 md:flex-row justify-between">
          <h2 className="text-headline3 lg:text-headline2 text-secondary/95 font-nunito font-bold">
            NDR Tracking
          </h2>
        </div>

        {/* Filters Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <DateInput
            label="Date Filter"
            placeholder="Select From & To Date"
            mode="range"
            value={filters.date}
            onChange={handleDateChange}
          />
          <InputField
            label="Order ID"
            placeholder="Enter Order ID separated by commas"
            value={filters.orderId}
            onChange={(value) => handleChange("orderId", value)}
          />
          <DropDownInput
            label="Courier Name"
            placeholder="Select Courier Name"
            options={courierOptions}
            value={filters.courierName} // ✅ Match filter state key
            onChange={(value) => handleChange("courierName", value)} // ✅ Match filter state key
          />

          <DropDownInput
            label="Channel Name"
            placeholder="Select Channel Name"
            options={channelOptions}
            value={filters.channelName}
            onChange={(value) => handleChange("channelName", value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <InputField
            label="AWBs"
            placeholder="Enter AWBs separated by comma"
            value={filters.awbNumber} // 🔄 Changed from filters.awbsID to filters.awbNumber
            onChange={(value) => handleChange("awbNumber", value)} // 🔄 Changed from "awbsID" to "awbNumber
          />
          <RadioInput
            label="Payment Mode"
            name="payment_mode"
            options={paymentOptions}
            selectedValue={filters.paymentMode}
            onChange={(value) => handleChange("paymentMode", value)}
          />
          <div className="flex flex-row gap-2 justify-start items-end">
            <Button
              variant="default"
              size="md"
              onClick={handleApply}
              className="h-12"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              className="h-12"
              onClick={handleReset}
            >
              Reset
            </Button>
          </div>
        </div>
        <MiniTabs
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={handleTabChange}
        />
        {/* Bulk NDR Sheet */}

        {selectedRows.length > 0 && (
          <Sheet>
            <SheetTrigger asChild>
              <div>
                <OptionGroup
                  options={bulkOptions}
                  selectedCount={selectedRows.length}
                  onClick={() => {}}
                />
              </div>
            </SheetTrigger>
            <SheetContent aria-describedby="">
              <SheetHeader>
                <SheetTitle>NDR Submit Form</SheetTitle>
                <NDRForm ids={selectedRows.map((sh) => sh._id)} />
              </SheetHeader>
            </SheetContent>
          </Sheet>
        )}
        <DataTable
          columns={columns}
          tittle="NDR"
          data={finalFilteredData}
          getSelectedRows={setSelectedRows}
          resetPage={resetPageCounter}
          isLoading={isLoading}
          actions={[
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "NDR",
                filename: `NDR_${new Date().toISOString().split("T")[0]}`,
              },
            },
          ]}
          isRowSelectable={(row) => row.ndrStatus === "Exception"}
        />
      </div>
    </SpaceWrapper>
  );
};

export default Ndr;
